import { DataSource } from "@angular/cdk/collections";
import { BehaviorSubject, catchError, Observable, of, Subject, switchMap, takeUntil, tap } from "rxjs";

export interface ITableParams {
  page: number;
  pageSize: number;
  [key: string]: unknown;
}

/**
 * Generic backend-driven DataSource.
 * Instantiate it by providing a data function and a count function.
 */
export class BackendTableDataSource<T> extends DataSource<T> {
  private dataSubject = new BehaviorSubject<T[]>([]);
  private countSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  public loading$ = this.loadingSubject.asObservable();
  public count$ = this.countSubject.asObservable();

  constructor(
    private dataFn: (params: ITableParams) => Observable<T[]>,
    private countFn: (params: ITableParams) => Observable<number>
  ) {
    super();
  }

  connect(): Observable<T[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(): void {
    this.dataSubject.complete();
    this.countSubject.complete();
    this.loadingSubject.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads the datasource.
   */
  public loadData(params: ITableParams): void {
    this.loadingSubject.next(true);

    this.countFn(params)
      .pipe(
        takeUntil(this.onDestroy$),
        catchError(() => of(0)),
        tap((count) => this.countSubject.next(count)),
        switchMap(() => this.dataFn(params).pipe(catchError(() => of([]))))
      )
      .subscribe((data) => {
        this.dataSubject.next(data);
        this.loadingSubject.next(false);
      });
  }

  public getCount(): number {
    return this.countSubject.value;
  }

  public getData(): T[] {
    return this.dataSubject.value;
  }
}
